import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	Box,
	Button,
	Typography,
	TextField,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppLayout from "../../components/AppLayout";
import CustomAutocomplete from "../../components/CustomAutocomplete";
import { VendaService } from "../../service/venda_service";
import { ClienteService } from "../../service/cliente_service";
import ProductSelectionModal from "./ProductSelectionModal";
import { useUser } from "../../context/UserContext";
import { hojeISO } from "../../utils/dateUtils";

function VendasCad() {
	const navigate = useNavigate();
	const { id } = useParams();
	const isEdit = Boolean(id);
	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");

	// userId vem do perfil mantido em memória pelo UserContext.
	const { user } = useUser();
	const userId = user?.userId;

	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	// Form data
	const [clienteId, setClienteId] = useState("");
	const [dataExpedicao, setDataExpedicao] = useState(hojeISO());
	const [notaFiscal, setNotaFiscal] = useState("");
	const [observacao, setObservacao] = useState("");

	// Lists
	const [clientes, setClientes] = useState([]);
	const [selectedCliente, setSelectedCliente] = useState(null);

	// Items
	const [itensSelecionados, setItensSelecionados] = useState([]);

	// Modal
	const [openProductModal, setOpenProductModal] = useState(false);

	// Computed values
	const [valorTotal, setValorTotal] = useState(0);

	const loadClientes = useCallback(async () => {
		try {
			const clienteService = new ClienteService();
			const result = await clienteService.getAll(unidadeDistribuidoraId, 1, 100, "");
			setClientes(result.data || []);
		} catch (error) {
			console.error("Erro ao carregar clientes:", error);
			alert(error.message || "Erro ao carregar clientes");
		}
	}, [unidadeDistribuidoraId]);

	const loadVenda = useCallback(async () => {
		setLoading(true);
		try {
			const vendaService = new VendaService();
			const venda = await vendaService.getById(id);

			setClienteId(venda.clienteId);
			setSelectedCliente(venda.cliente);
			setDataExpedicao(venda.dataExpedicao.split("T")[0]);
			setNotaFiscal(venda.notaFiscal || "");
			setObservacao(venda.observacao || "");

			// Carregar itens
			const itens = venda.itens.map((item) => ({
				ordemProducaoId: item.ordemProducaoId,
				quantidade: item.quantidade,
				volumes: item.volumes,
				preco: item.preco,
				ordemProducao: item.ordemProducao,
			}));
			setItensSelecionados(itens);
		} catch (error) {
			console.error("Erro ao carregar venda:", error);
			alert(error.message || "Erro ao carregar venda");
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => {
		loadClientes();
	}, [loadClientes]);

	useEffect(() => {
		if (id && isEdit) {
			loadVenda();
		}
	}, [id, isEdit, loadVenda]);

	// Calcular valor total sempre que itens mudarem
	useEffect(() => {
		const total = itensSelecionados.reduce(
			(sum, item) => sum + item.quantidade * item.preco,
			0
		);
		setValorTotal(total);
	}, [itensSelecionados]);

	const handleClienteChange = (newValue) => {
		setClienteId(newValue?.id || "");
		setSelectedCliente(newValue);
	};

	const handleOpenProductModal = () => {
		setOpenProductModal(true);
	};

	const handleCloseProductModal = () => {
		setOpenProductModal(false);
	};

	const handleAddProducts = (produtos) => {
		// Adicionar novos produtos aos itens já selecionados
		setItensSelecionados([...itensSelecionados, ...produtos]);
		handleCloseProductModal();
	};

	const handleRemoveItem = (index) => {
		const novosItens = itensSelecionados.filter((_, i) => i !== index);
		setItensSelecionados(novosItens);
	};

	const handleEditQuantidade = async (index, novaQuantidade) => {
		const quantidade = parseFloat(novaQuantidade) || 0;

		const novosItens = [...itensSelecionados];
		const item = novosItens[index];

		// Validar que a quantidade não é negativa
		if (quantidade < 0) {
			alert("A quantidade não pode ser negativa");
			return;
		}

		// Atualizar quantidade
		item.quantidade = quantidade;

		// Recalcular volumes
		const emb = item.ordemProducao?.embalagemProdutoFinal;
		if (emb && emb.quantidade > 0) {
			item.volumes = Math.floor(quantidade / emb.quantidade);
		}

		setItensSelecionados(novosItens);
	};

	const handleEditPreco = (index, novoPreco) => {
		const preco = parseFloat(novoPreco) || 0;

		// Validar que o preço não é negativo
		if (preco < 0) {
			alert("O preço não pode ser negativo");
			return;
		}

		const novosItens = [...itensSelecionados];
		const produtoFinalId = novosItens[index].ordemProducao?.produtoFinal?.id;

		// Auto-fill price for all items of the same product type
		novosItens.forEach((item, i) => {
			if (item.ordemProducao?.produtoFinal?.id === produtoFinalId) {
				novosItens[i].preco = preco;
			}
		});

		setItensSelecionados(novosItens);
	};

	const handleSave = async () => {
		// Validações
		if (!clienteId) {
			alert("Por favor, selecione um cliente");
			return;
		}

		if (!dataExpedicao) {
			alert("Por favor, informe a data de expedição");
			return;
		}

		if (itensSelecionados.length === 0) {
			alert("Por favor, adicione pelo menos um produto à venda");
			return;
		}

		// Validar userId
		if (!userId) {
			alert("Erro: Usuário não identificado. Por favor, faça login novamente.");
			return;
		}

		setSaving(true);
		try {
			const vendaService = new VendaService();

			const payload = {
				id: isEdit ? id : undefined,
				unidadeDistribuidoraId,
				clienteId,
				dataExpedicao,
				notaFiscal: notaFiscal || null,
				observacao: observacao || null,
				responsavel_cadastro_id: userId,
				responsavel_atualizacao_id: userId,
				itens: itensSelecionados.map((item) => ({
					ordemProducaoId: item.ordemProducaoId,
					quantidade: item.quantidade,
					volumes: item.volumes,
					preco: item.preco,
				})),
			};

			await vendaService.save(payload);
			navigate("/vendas");
		} catch (error) {
			console.error("Erro ao salvar:", error);
			alert(error.message || "Erro ao salvar venda");
		} finally {
			setSaving(false);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
	};

	const formatCurrency = (value) => {
		return value.toLocaleString("pt-BR", {
			style: "currency",
			currency: "BRL",
		});
	};

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "100vh",
				}}
			>
				<Typography>Carregando...</Typography>
			</Box>
		);
	}

	return (
		<AppLayout currentPage="vendas">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					{/* Título */}
					<Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
						<IconButton onClick={() => navigate("/vendas")} sx={{ color: "#666" }}>
							<ArrowBackIcon />
						</IconButton>
						<Typography
							variant="h5"
							sx={{
								color: "#666",
								fontWeight: 500,
								fontSize: "20px",
							}}
						>
							{isEdit ? "Editar Venda" : "Nova Venda"}
						</Typography>
					</Box>

					{/* Container branco */}
					<Box
						sx={{
							bgcolor: "white",
							borderRadius: "4px",
							boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
							overflow: "hidden",
						}}
					>
						{/* Formulário */}
						<Box sx={{ px: 6, py: 4 }}>
							{/* Título da seção */}
							<Typography
								variant="subtitle1"
								sx={{ color: "#1a9e8f", fontWeight: 600, mb: 2, fontSize: "16px" }}
							>
								Dados da Venda
							</Typography>

							{/* Linha de campos do formulário */}
							<Box sx={{ display: "flex", gap: 2, mb: 3 }}>
								<Box sx={{ flex: "0 0 30%" }}>
									<CustomAutocomplete
										size="small"
										options={clientes}
										value={selectedCliente}
										onChange={(newValue) => handleClienteChange(newValue)}
										getOptionLabel={(option) => {
											const nome = option.nome || "";
											const fantasia = option.nomeFantasia ? ` (${option.nomeFantasia})` : "";
											return `${nome}${fantasia}`;
										}}
										getOptionId={(option) => option.id}
										noOptionsText="Nenhum cliente disponível"
										label="Cliente"
										required
										placeholder="Buscar cliente..."
									/>
								</Box>

								<Box sx={{ flex: "0 0 15%" }}>
									<TextField
										fullWidth
										label="Data de Expedição *"
										type="date"
										value={dataExpedicao}
										onChange={(e) => setDataExpedicao(e.target.value)}
										InputLabelProps={{ style: { fontSize: "15px" }, shrink: true }}
										inputProps={{ style: { fontSize: "15px" } }}
										size="small"
									/>
								</Box>

								<Box sx={{ flex: "0 0 20%" }}>
									<TextField
										fullWidth
										label="Nota Fiscal"
										value={notaFiscal}
										onChange={(e) => setNotaFiscal(e.target.value)}
										InputLabelProps={{ style: { fontSize: "15px" } }}
										inputProps={{ style: { fontSize: "15px" } }}
										size="small"
									/>
								</Box>

								<Box sx={{ flex: "0 0 35%" }}>
									<TextField
										fullWidth
										label="Observação"
										value={observacao}
										onChange={(e) => setObservacao(e.target.value)}
										InputLabelProps={{ style: { fontSize: "15px" } }}
										inputProps={{ style: { fontSize: "15px" } }}
										size="small"
										multiline
									/>
								</Box>
							</Box>

							{/* Produtos da Venda */}
							<Typography
								variant="subtitle1"
								sx={{
									color: "#1a9e8f",
									fontWeight: 600,
									mb: 2,
									mt: 4,
									fontSize: "16px",
								}}
							>
								Produtos da Venda
							</Typography>

							<Box sx={{ mb: 2 }}>
								<Button
									variant="contained"
									startIcon={<AddIcon />}
									onClick={handleOpenProductModal}
									sx={{
										bgcolor: "#1a9e8f",
										"&:hover": {
											bgcolor: "#138a7d",
										},
										textTransform: "none",
										fontSize: "15px",
									}}
								>
									Adicionar Produto
								</Button>
							</Box>

							{/* Tabela de produtos adicionados */}
							{itensSelecionados.length > 0 && (
								<Box sx={{ mb: 3 }}>
									<TableContainer
										sx={{
											border: "1px solid #e0e0e0",
											borderRadius: "4px",
										}}
									>
										<Table size="small">
											<TableHead>
												<TableRow sx={{ bgcolor: "#f5f5f5" }}>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
														Produto Final
													</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
														Quantidade
													</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
														Embalagem
													</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
														Data Embalagem
													</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
														Volumes
													</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
														Preço Unit.
													</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
														Lote
													</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px", width: "80px" }}>
														Ações
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{itensSelecionados.map((item, index) => {
													const op = item.ordemProducao;
													const cultivo = op.produtoFinal?.cultivo;
													const cultura = cultivo?.cultura || "-";
													const variedade = cultivo?.variedade ? ` - ${cultivo.variedade}` : "";
													const produtoDescricao = `${cultura}${variedade}`;

													const emb = op.embalagemProdutoFinal;
													const embDescricao = emb
														? `${emb.descricao} (${emb.quantidade} ${emb.unidadeMedida?.sigla || ""})`
														: "-";

													return (
														<TableRow key={index}>
															<TableCell sx={{ fontSize: "14px" }}>
																{produtoDescricao}
															</TableCell>
															<TableCell sx={{ fontSize: "14px", width: "120px" }}>
																<TextField
																	size="small"
																	type="number"
																	value={item.quantidade}
																	onChange={(e) => handleEditQuantidade(index, e.target.value)}
																	inputProps={{
																		style: { fontSize: "14px" },
																		min: 0,
																		step: 0.01,
																	}}
																	sx={{ width: "100%" }}
																/>
															</TableCell>
															<TableCell sx={{ fontSize: "14px" }}>
																{embDescricao}
															</TableCell>
															<TableCell sx={{ fontSize: "14px" }}>
																{formatDate(op.data)}
															</TableCell>
															<TableCell sx={{ fontSize: "14px" }}>
																{item.volumes}
															</TableCell>
															<TableCell sx={{ fontSize: "14px", width: "120px" }}>
																<TextField
																	size="small"
																	type="number"
																	value={item.preco}
																	onChange={(e) => handleEditPreco(index, e.target.value)}
																	inputProps={{
																		style: { fontSize: "14px" },
																		min: 0,
																		step: 0.01,
																	}}
																	sx={{ width: "100%" }}
																/>
															</TableCell>
															<TableCell sx={{ fontSize: "14px" }}>
																{op.loteRastreabilidade || "-"}
															</TableCell>
															<TableCell align="center">
																<IconButton
																	size="small"
																	onClick={() => handleRemoveItem(index)}
																	sx={{ color: "#ff4d4d" }}
																>
																	<DeleteIcon fontSize="small" />
																</IconButton>
															</TableCell>
														</TableRow>
													);
												})}
											</TableBody>
										</Table>
									</TableContainer>
								</Box>
							)}

							{/* Valor Total */}
							{itensSelecionados.length > 0 && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
									<Typography
										variant="h6"
										sx={{
											color: "#1a9e8f",
											fontWeight: 600,
											fontSize: "18px",
										}}
									>
										Valor Total: {formatCurrency(valorTotal)}
									</Typography>
								</Box>
							)}
						</Box>

						{/* Botão Salvar */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								px: 6,
								pb: 4,
								pt: 0,
							}}
						>
							<Button
								variant="contained"
								onClick={handleSave}
								disabled={saving}
								sx={{
									bgcolor: "#1a9e8f",
									"&:hover": {
										bgcolor: "#138a7d",
										boxShadow: "0 2px 6px rgba(23, 162, 184, 0.4)",
									},
									"&:disabled": {
										bgcolor: "#ccc",
									},
									textTransform: "none",
									px: 5,
									py: 1.5,
									fontSize: "15px",
									fontWeight: 500,
									borderRadius: "4px",
									boxShadow: "none",
								}}
							>
								{saving ? "Salvando..." : isEdit ? "Atualizar" : "Cadastrar"}
							</Button>
						</Box>
					</Box>
			</Box>

			{/* Modal de seleção de produtos */}
			<ProductSelectionModal
				open={openProductModal}
				onClose={handleCloseProductModal}
				onSelect={handleAddProducts}
				unidadeDistribuidoraId={unidadeDistribuidoraId}
				alreadySelected={itensSelecionados.map((item) => item.ordemProducaoId)}
			/>
		</AppLayout>
	);
}

export default VendasCad;
